import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Mail,
  Phone,
  MapPin,
  Send,
  CheckCircle2
} from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', email: '', subject: '', message: '' });
    }, 5000);
  };

  return (
    <div className="min-h-screen pt-24 pb-20 bg-dark-bg">
      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-96 h-96 bg-gold/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-gold/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gold text-xs font-medium uppercase tracking-widest mb-4">
            Let's Connect
          </p>
          
          <h1 className="text-4xl md:text-5xl font-bold text-warm-white mb-6 tracking-tight">
            Get in Touch
          </h1>
          
          <p className="text-lg text-warm-white/60 max-w-2xl mx-auto leading-relaxed">
            Have a question, topic suggestion, or partnership inquiry? I'd love
            to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-bold text-warm-white mb-8 tracking-tight">
                Send a Message
              </h2>

              {!isSubmitted ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-warm-white/70 mb-2 block text-xs uppercase tracking-widest">
                      Your Name
                    </Label>
                    <Input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      className="h-12 bg-dark-card border-dark-border text-warm-white placeholder:text-warm-white/40 focus:border-gold focus:ring-gold"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-warm-white/70 mb-2 block text-xs uppercase tracking-widest">
                      Email Address
                    </Label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                      className="h-12 bg-dark-card border-dark-border text-warm-white placeholder:text-warm-white/40 focus:border-gold focus:ring-gold"
                    />
                  </div>

                  <div>
                    <Label
                      htmlFor="subject"
                      className="text-warm-white/70 mb-2 block text-xs uppercase tracking-widest"
                    >
                      Subject
                    </Label>
                    <Input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      placeholder="What's this about?"
                      className="h-12 bg-dark-card border-dark-border text-warm-white placeholder:text-warm-white/40 focus:border-gold focus:ring-gold"
                    />
                  </div>

                  <div>
                    <Label
                      htmlFor="message"
                      className="text-warm-white/70 mb-2 block text-xs uppercase tracking-widest"
                    >
                      Message
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Tell me more..."
                      rows={6}
                      className="bg-dark-card border-dark-border text-warm-white placeholder:text-warm-white/40 focus:border-gold focus:ring-gold resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 bg-gold hover:bg-gold/90 text-dark-bg font-semibold text-xs uppercase tracking-wider transition-all duration-300"
                  >
                    <Send className="mr-2 w-4 h-4" />
                    Send Message
                  </Button>
                </form>
              ) : (
                <Card className="p-10 text-center border border-gold/30 bg-dark-card">
                  <div className="w-16 h-16 bg-gold flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-8 h-8 text-dark-bg" />
                  </div>
                  <h3 className="text-2xl font-bold text-warm-white mb-2 tracking-tight">
                    Message Sent
                  </h3>
                  <p className="text-warm-white/60">
                    Thanks for reaching out. I'll get back to you within 24-48
                    hours.
                  </p>
                </Card>
              )}
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold text-warm-white mb-8 tracking-tight">
                Other Ways to Connect
              </h2>

              <div className="space-y-6 mb-10">
                <Card className="p-6 border border-dark-border bg-dark-card hover:border-gold/30 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gold flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-dark-bg" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-warm-white mb-1">
                        Email
                      </h3>
                      <a
                        href="mailto:ravikanth.jagarlapudi@gmail.com"
                        className="text-gold hover:text-gold/80 break-all text-sm"
                      >
                        ravikanth.jagarlapudi@gmail.com
                      </a>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 border border-dark-border bg-dark-card hover:border-gold/30 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gold flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-dark-bg" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-warm-white mb-1">
                        Phone
                      </h3>
                      <p className="text-warm-white/60 text-sm">Available upon request</p>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 border border-dark-border bg-dark-card hover:border-gold/30 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gold flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-dark-bg" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-warm-white mb-1">
                        Location
                      </h3>
                      <p className="text-warm-white/60 text-sm">Remote / Global</p>
                    </div>
                  </div>
                </Card>
              </div>

              <div className="bg-dark-card border border-dark-border p-8">
                <h3 className="text-lg font-bold text-warm-white mb-3 tracking-tight">
                  Response Time
                </h3>
                <p className="text-warm-white/60 leading-relaxed mb-4 text-sm">
                  I typically respond to all inquiries within 24-48 hours. For
                  urgent matters, please mention "URGENT" in your subject line.
                </p>
                <p className="text-xs text-warm-white/50 uppercase tracking-wider">
                  Best for: Topic suggestions, guest appearances, partnerships, media inquiries
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-dark-card border-t border-dark-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-warm-white mb-16 text-center tracking-tight">
            Common Questions
          </h2>

          <div className="space-y-6">
            <Card className="p-6 border border-dark-border bg-dark-bg">
              <h3 className="text-lg font-bold text-warm-white mb-2">
                Can I suggest a topic for an episode?
              </h3>
              <p className="text-warm-white/60 text-sm">
                Absolutely! I love hearing from listeners. Use the contact form
                above or email me directly with your topic ideas.
              </p>
            </Card>

            <Card className="p-6 border border-dark-border bg-dark-bg">
              <h3 className="text-lg font-bold text-warm-white mb-2">
                Are you available for guest appearances?
              </h3>
              <p className="text-warm-white/60 text-sm">
                Yes! I'm open to podcast interviews, conference talks, and
                webinars. Please include details about your event in your
                message.
              </p>
            </Card>

            <Card className="p-6 border border-dark-border bg-dark-bg">
              <h3 className="text-lg font-bold text-warm-white mb-2">
                How can I sponsor the podcast?
              </h3>
              <p className="text-warm-white/60 text-sm">
                I'd love to explore partnership opportunities. Please visit the{' '}
                <a href="/partners" className="text-gold hover:text-gold/80">
                  Partners page
                </a>{' '}
                for more information or reach out directly.
              </p>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
